import { pageTitle } from '@palaccount/config';
import type { FinStatementRow, StatementKind } from '@palaccount/contracts';
import { ArrowLeftRight, CheckCircle2, AlertCircle, FileDown, Loader2, Printer, Sheet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { roundToWholeUnits } from '@/lib/money';
import { useMe } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useFinancialReport } from './api';
import { buildReportGrid, downloadXlsx, downloadReportPdf, printReportDocument } from './export';
import { presetDates, useReportWindow, type PresetKey } from './report-shell';
import { ReportsTabs } from './reports-tabs';

/**
 * DSH-08 — Financial Reports. The fourth report screen: it shares the
 * toolbar and the period presets but NOT the More filters band, the four
 * summary cards or the View detailed data fold (§DSH-10) — a statement is
 * not a filtered list.
 *
 * Two rules this screen gets right that a naive build gets wrong:
 *
 *   §5d/BR-DSH-08-06 — money renders in ACCOUNTING PARENTHESES. A negative
 *   is (96,000), never −96,000. This is the one screen in PART 14 that does
 *   not use the shared <Amount>, because <Amount> writes a minus sign.
 *
 *   §5f/BR-DSH-08-07 — a variance cell is coloured by FAVOURABILITY, not by
 *   sign. The tone arrives from the server (varTone), which is what keeps
 *   Cost of Goods Sold, Operating Expenses and both liability sections red
 *   on a rise. No client ever recomputes it from the number.
 */

const STATEMENTS: readonly StatementKind[] = ['pnl', 'balance', 'cashflow'];

/** §5b — four choices here; This Year is the delivered default. */
const PERIODS: readonly PresetKey[] = ['thisMonth', 'thisQuarter', 'thisYear', 'custom'];

type CompareKind = 'previous' | 'lastYear' | 'custom' | 'none';
const COMPARISONS: readonly CompareKind[] = ['previous', 'lastYear', 'custom', 'none'];

/**
 * BR-DSH-08-06: "-96000.00" → "(96,000)". Statement figures round to whole
 * currency units (BR-DSH-10-15); the sign becomes brackets, never a dash.
 */
export function accountingMoney(value: string): string {
  // Rounded to whole units through Money (N-6, half-up), not Math.round on a
  // float: this is the last step of a statement figure, and a statement's job
  // is to agree with the ledger to the unit.
  const whole = roundToWholeUnits(value).replace('-', '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // A figure that rounds away to nothing is written bare: "(0)" would claim a
  // loss the statement does not have.
  return value.startsWith('-') && grouped !== '0' ? `(${grouped})` : grouped;
}

/** §5i — the two Cash Flow rows that are balances rather than flows. */
const BALANCE_ROWS = new Set(['openingCash', 'closingCash']);

const TONE_CLASS: Readonly<Record<string, string>> = {
  good: 'text-emerald-700 dark:text-emerald-400',
  bad: 'text-red-600 dark:text-red-400',
  flat: 'text-muted-foreground',
};

/** §5j — the plain-words gloss every tile carries, for every user (§9). */
const RATIO_GLOSS: Readonly<Record<string, string>> = {
  grossMargin: 'reports.glossGrossMargin',
  netMargin: 'reports.glossNetMargin',
  operatingMargin: 'reports.glossOperatingMargin',
  currentRatio: 'reports.glossCurrentRatio',
  debtToEquity: 'reports.glossDebtToEquity',
  workingCapital: 'reports.glossWorkingCapital',
  operatingCashFlowMargin: 'reports.glossOcfMargin',
  netChangeInCash: 'reports.glossNetChangeInCash',
  closingCashBalance: 'reports.glossClosingCash',
};

export function FinancialReportPage() {
  const { t, i18n } = useTranslation();
  const { today, from, to, setFrom, setTo } = useReportWindow('thisYear');
  const [statement, setStatement] = useState<StatementKind>('pnl');
  /**
   * BR-DSH-08-19: "The Period control must change the figures shown."
   *
   * ERR-DSH-08-02 / ERR-DSH-08-10 are what it corrects: "the Period control is
   * INERT; no figure varies by period." A dropdown that moves and changes
   * nothing is the quietest form of the defect this build keeps meeting — the
   * reader believes they are looking at this quarter and are looking at
   * whatever the fixture held. Here `period` drives `from`/`to` through
   * `useReportWindow`, those two are the query key, and every statement is
   * re-derived from the ledger over that window; there is no cached figure for
   * the control to fail to reach.
   */
  const [period, setPeriod] = useState<PresetKey>('thisYear');
  const [compare, setCompare] = useState<CompareKind>('previous');
  // BR-DSH-08-20: Custom names its OWN dates rather than silently replaying
  // the previous period — the prototype's trap, refused here.
  /*
   * CALENDAR YEARS, not amounts: DSH-08's default comparison window is the
   * same dates one year earlier, so the four-digit year is decremented and the
   * rest of the ISO date carried across unchanged.
   */
  // eslint-disable-next-line money/no-raw-money-arithmetic -- calendar year, not an amount
  const [compareFrom, setCompareFrom] = useState(`${Number(from.slice(0, 4)) - 1}${from.slice(4)}`);
  // eslint-disable-next-line money/no-raw-money-arithmetic -- calendar year, not an amount
  const [compareTo, setCompareTo] = useState(`${Number(to.slice(0, 4)) - 1}${to.slice(4)}`);

  const { data, isPending, isError } = useFinancialReport({
    from,
    to,
    statement,
    compare,
    ...(compare === 'custom' ? { compareFrom, compareTo } : {}),
  });

  useEffect(() => {
    document.title = pageTitle(t('reports.tabFinancial'));
  }, [t]);

  const comparing = compare !== 'none';
  /**
   * §5c — the shared grid has exactly two shapes, and both columns move
   * together: four (label, Current, comparison, **Var %**) while comparing,
   * two otherwise. One flag decides the header, every row and every export, so
   * §5c's *"a build must not let the two get out of step"* is structural here
   * rather than three call sites agreeing.
   *
   * ERR-DSH-08-04 IS THAT COLUMN LIST, READ FROM THE USER'S SIDE: *"Looked for
   * a **Variance $** column — only Var % is rendered — work it out from the two
   * columns."* All three clauses are true of this build and each is decided
   * here:
   *  - there is no Variance $ column, because §5c's normative grid has four
   *    cells and none of them is one;
   *  - Var % is the variance that renders (`reports.colVar`, and it is the only
   *    `colVar*` key the dictionary holds);
   *  - and the remedy is available — "the two columns" the reader is told to
   *    subtract are Current and the comparison, which appear if and only if
   *    Var % does, off this same flag. A build that dropped the comparison
   *    column while keeping Var % would satisfy the first two clauses and leave
   *    the §14 row with no remedy to offer, which is the failure worth naming.
   *
   * This is a §14 row RECORDED as the reader's surprise, not repaired: nothing
   * in DSH-08's rule block asks for the absolute column, so adding one would be
   * a build inventing a fifth cell §5c does not have. ERR-DSH-08-12 next door
   * (`financial-statements.service.ts`) is the half that WAS a defect — a
   * figure computed and thrown away — and that one is gone.
   */
  const gridTemplate = comparing ? '2.2fr 1fr 1fr 1.1fr' : '2.6fr 1.2fr';

  /** An Arabic reader gets the account's own Arabic name where the chart carries one. */
  const rowLabel = useMemo(() => {
    const arabic = i18n.language.startsWith('ar');
    return (row: FinStatementRow): string => {
      // Section/subtotal labels are keys the dictionary owns; account lines
      // carry the name the user typed into the chart.
      if (row.accountId === null && row.code === null) return t(`reports.fin_${row.label}`);
      return (arabic && row.labelAr) || row.label;
    };
  }, [i18n.language, t]);

  /**
   * DSH-08's grid, in the shape Print, Excel and PDF all take.
   *
   * BR-DSH-08-22 / BR-DSH-10-24: the export carries the columns in force —
   * which here means the comparison pair appears only when comparing. The
   * columns are declared inline rather than through `buildReportGrid`'s
   * picker resolution, because a statement has no column picker: its shape is
   * decided by the comparison switch and by nothing else.
   */
  const rtl = i18n.language.startsWith('ar');
  const { data: me } = useMe();
  const [pdfState, setPdfState] = useState<'idle' | 'working' | 'failed'>('idle');
  const statementColumns = comparing
    ? [t('reports.colLine'), t('reports.colCurrent'), t(`reports.compare_${compare}`), t('reports.colVar')]
    : [t('reports.colLine'), t('reports.colCurrent')];
  const statementRows = (data?.rows ?? []).map((row) =>
    comparing
      ? [rowLabel(row), row.current ?? '', row.comparison ?? '', row.varPct === null ? '—' : `${row.varPct}%`]
      : [rowLabel(row), row.current ?? ''],
  );
  const { grid, types } = buildReportGrid<readonly string[]>({
    title: t(`reports.statement_${statement}`),
    from,
    to,
    // BR-DSH-10-16's "filters applied" row list is deliberately absent here:
    // DSH-08 "has no from-and-to dates and no More filters band" is half
    // wrong for this build (it has the window) but the filter half holds —
    // a statement is not a filtered list, and the one choice that shapes the
    // figures (the comparison) is already printed in the column headers the
    // grid carries. The heading block's other elements — company, statement
    // name, window, run time, user — ride the shared report template.
    cards: [],
    columnDefs: statementColumns.map((label, index) => ({
      id: String(index),
      label,
      // Column 0 is the account line; everything after it is a figure.
      numeric: index > 0,
    })),
    selected: statementColumns.map((_, index) => String(index)),
    defaults: statementColumns.map((_, index) => String(index)),
    rows: statementRows,
    // eslint-disable-next-line money/no-raw-money-arithmetic -- a column INDEX, not an amount
    value: (row, id) => row[Number(id)] ?? '',
  });

  return (
    <div className="flex flex-col gap-4">
      <ReportsTabs />
      <h1 className="text-xl font-semibold">{t('reports.tabFinancial')}</h1>

      {/* Band 1 — the toolbar plus the three-way statement selector. */}
      <div className="no-print flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!data}
          onClick={() =>
            printReportDocument({
              grid,
              locale: rtl ? 'ar' : 'en',
              company: { name: me?.tenant.name ?? '' },
              generatedBy: me?.user.name ?? null,
            })
          }
        >
          <Printer className="me-1 h-4 w-4" />
          {t('reports.print')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            downloadXlsx(
              `${statement}-${from}-${to}.xlsx`,
              t(`reports.statement_${statement}`),
              grid.columns,
              grid.rows,
              types,
              rtl,
            )
          }
        >
          <Sheet className="me-1 h-4 w-4" />
          {t('reports.excel')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!data || pdfState === 'working'}
          onClick={async () => {
            setPdfState('working');
            try {
              await downloadReportPdf(grid, rtl ? 'ar' : 'en');
              setPdfState('idle');
            } catch {
              setPdfState('failed');
            }
          }}
        >
          {pdfState === 'working' ? (
            <Loader2 className="me-1 h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="me-1 h-4 w-4" />
          )}
          {t('reports.pdf')}
        </Button>
        {pdfState === 'failed' ? (
          <span role="alert" className="text-xs text-destructive">
            {t('reports.pdfFailed')}
          </span>
        ) : null}
        <span className="mx-1 h-6 w-px bg-border" />
        {STATEMENTS.map((kind) => (
          <button
            key={kind}
            type="button"
            aria-pressed={statement === kind}
            // §5a: a SOFTER selected style than the solid tabs elsewhere.
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm font-medium',
              statement === kind ? 'border-primary bg-primary/10 text-foreground' : 'text-muted-foreground',
            )}
            onClick={() => setStatement(kind)}
          >
            {t(`reports.statement_${kind}`)}
          </button>
        ))}
      </div>

      {/* Band 2 — period and comparison. */}
      <div className="no-print flex flex-wrap items-center gap-2">
        <label className="text-xs text-muted-foreground" htmlFor="fin-period">
          {t('reports.period')}
        </label>
        <select
          id="fin-period"
          className="rounded-md border bg-background px-2 py-1 text-sm"
          value={period}
          onChange={(e) => {
            const next = e.target.value as PresetKey;
            setPeriod(next);
            // BR-DSH-08-19: the period control CHANGES THE FIGURES here.
            if (next !== 'custom') {
              const dates = presetDates(next, today);
              setFrom(dates.from);
              setTo(dates.to);
            }
          }}
        >
          {PERIODS.map((p) => (
            <option key={p} value={p}>
              {t(`reports.preset_${p}`)}
            </option>
          ))}
        </select>
        {period === 'custom' ? (
          <>
            <input
              aria-label={t('reports.from')}
              type="date"
              dir="ltr"
              className="rounded-md border bg-background px-2 py-1 text-sm"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <input
              aria-label={t('reports.to')}
              type="date"
              dir="ltr"
              className="rounded-md border bg-background px-2 py-1 text-sm"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </>
        ) : null}

        <label className="ms-2 text-xs text-muted-foreground" htmlFor="fin-compare">
          {t('reports.compareTo')}
        </label>
        <select
          id="fin-compare"
          className="rounded-md border bg-background px-2 py-1 text-sm"
          value={compare}
          onChange={(e) => setCompare(e.target.value as CompareKind)}
        >
          {COMPARISONS.map((c) => (
            <option key={c} value={c}>
              {t(`reports.compare_${c}`)}
            </option>
          ))}
        </select>
        {compare === 'custom' ? (
          <>
            <input
              aria-label={t('reports.compareFrom')}
              type="date"
              dir="ltr"
              className="rounded-md border bg-background px-2 py-1 text-sm"
              value={compareFrom}
              onChange={(e) => setCompareFrom(e.target.value)}
            />
            <input
              aria-label={t('reports.comparisonTo')}
              type="date"
              dir="ltr"
              className="rounded-md border bg-background px-2 py-1 text-sm"
              value={compareTo}
              onChange={(e) => setCompareTo(e.target.value)}
            />
          </>
        ) : null}
        {comparing ? (
          <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            <ArrowLeftRight className="h-3 w-3" />
            {t('reports.comparisonPill', { comparison: t(`reports.compare_${compare}`) })}
          </span>
        ) : null}
      </div>

      {isPending ? (
        <div className="h-80 animate-pulse rounded-md bg-muted" aria-busy />
      ) : isError || !data ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">{t('common.loadFailed')}</p>
      ) : (
        <>
          {/* Band 3 — the statement sheet. */}
          <Card>
            <CardContent className="overflow-x-auto pt-4">
              <div className="min-w-[560px]">
                <div
                  className="grid items-baseline gap-x-3 border-b-2 pb-1 text-xs uppercase tracking-wide text-muted-foreground"
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  <span />
                  <span className="text-end">{t('reports.colCurrent')}</span>
                  {comparing ? <span className="text-end">{t(`reports.compare_${compare}`)}</span> : null}
                  {comparing ? <span className="text-end">{t('reports.colVar')}</span> : null}
                </div>

                {data.rows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                ) : (
                  data.rows.map((row, index) => (
                    <StatementLine
                      key={`${row.label}-${row.code ?? index}`}
                      row={row}
                      label={rowLabel(row)}
                      comparing={comparing}
                      gridTemplate={gridTemplate}
                      /* BR-DSH-08-13: Cash Flow rows carry no code and never link. */
                      drillable={data.statement !== 'cashflow' && row.accountId !== null}
                    />
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* §5h — the balance check banner, Balance Sheet only. */}
          {data.balanced !== null ? (
            <div
              role="status"
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium',
                data.balanced ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700',
              )}
            >
              {data.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              {t(data.balanced ? 'reports.balanced' : 'reports.outOfBalance')}
            </div>
          ) : null}

          {/* Band 4 — the ratio strip, OUTSIDE the sheet card so it never
              scrolls away and never disappears on an empty statement. */}
          <div className="grid gap-[18px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
            {data.ratios.map((tile) => (
              <Card key={tile.key}>
                <CardContent className="pt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t(`reports.ratio_${tile.key}`)}
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {/* BR-DSH-08-16: an em dash, never an error or an infinity. */}
                    <bdi dir="ltr" className="font-mono tabular-nums">
                      {tile.value === null
                        ? '—'
                        : tile.kind === 'pct'
                          ? t('unit.percent', { value: tile.value })
                          : tile.kind === 'ratio'
                            ? `${tile.value}×`
                            : accountingMoney(tile.value)}
                    </bdi>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{t(RATIO_GLOSS[tile.key] ?? 'reports.glossFallback')}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** One statement row: §5d's shape, §5f's colour, §5e's drill-down. */
function StatementLine({
  row,
  label,
  comparing,
  gridTemplate,
  drillable,
}: {
  row: FinStatementRow;
  label: string;
  comparing: boolean;
  gridTemplate: string;
  drillable: boolean;
}) {
  const { t } = useTranslation();
  if (row.kind === 'section') {
    return (
      <p className="mt-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
    );
  }

  const heavy = row.kind === 'total';
  return (
    <div
      className={cn(
        'grid items-baseline gap-x-3 py-1 text-sm',
        row.kind === 'subtotal' && 'my-1 border-y font-semibold',
        heavy && 'my-1 rounded-md border-t-2 bg-primary/10 px-2 font-semibold text-foreground',
      )}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      <span className="flex items-baseline gap-2">
        {/*
          BR-DSH-08-12: "Every Profit & Loss and Balance Sheet line must show
          its account code and must make its account name a link." Both halves
          are these two elements, and both are driven by what the SERVER put on
          the row rather than by the statement kind: `code` is non-null exactly
          on the account lines (a section heading, a subtotal and a total each
          carry null and correctly show none), and `drillable` is
          `statement !== 'cashflow' && accountId !== null`, which is
          BR-DSH-08-13 read from the other end.

          WHERE the link goes is a defect the spec RECORDS rather than fixes.
          ERR-DSH-08-13: "Every line drills to the whole Chart of Accounts
          rather than to that account's ledger" — §5e adds that the "View
          ledger" label exists in the product's text and is never shown, so the
          filtered drill-down was designed and not built. BR-DSH-08-12 requires
          a link and says nothing about its destination, so the rule is met;
          the missing drill-down is reported, not quietly claimed.

          ERR-DSH-08-05 IS THE SAME DEFECT SEEN FROM THE READER'S CHAIR, and it
          is named here beside -13 rather than left unattached, because §14's
          row is a statement about this `<Link>` and nothing else: "Clicked an
          account name expecting its ledger — the whole Chart of Accounts opens
          — find the account in the chart and open its ledger there." Both
          columns describe what the destination below actually does. §13's
          walkthrough narrates the same click ("Chart of Accounts opens — not a
          filtered ledger for account 6200 ... which means she has to find the
          account herself").

          WHY THE REMEDY COLUMN IS THE PART THAT HAD TO BE CHECKED: "find the
          account in the chart" is only advice a reader can follow if the code
          travels with them, and `row.code` is rendered immediately to the left
          of this link. A build that dropped the code cell would leave the
          reader on a chart of hundreds of accounts with only a name — the same
          click, the same screen, and the row's remedy no longer available.
          That is what makes this a citation about behaviour rather than a note
          about a missing feature.
        */}
        {row.code ? <bdi dir="ltr" className="font-mono text-xs text-muted-foreground">{row.code}</bdi> : null}
        {drillable ? (
          <Link to="/accounting/chart" className="hover:text-primary hover:underline">
            {label}
          </Link>
        ) : (
          <span className={cn(row.kind === 'line' && 'ps-1')}>{label}</span>
        )}
      </span>
      <bdi dir="ltr" className="text-end font-mono tabular-nums">
        {row.current === null ? '' : accountingMoney(row.current)}
      </bdi>
      {comparing ? (
        <bdi dir="ltr" className="text-end font-mono tabular-nums text-muted-foreground">
          {row.comparison === null ? '' : accountingMoney(row.comparison)}
        </bdi>
      ) : null}
      {comparing ? (
        <bdi dir="ltr" className={cn('text-end font-mono tabular-nums', TONE_CLASS[row.varTone ?? 'flat'])}>
          {/* Two different silences, and the spec wants them told apart:
              BR-DSH-08-14 leaves the Opening/Closing Cash cells EMPTY because a
              balance is not a flow, while BR-DSH-08-09 prints an em dash when
              the comparison figure is zero. Both arrive as a null varPct, so
              the balance rows are named here. */}
          {BALANCE_ROWS.has(row.label) ? '' : row.varPct === null ? '—' : t('unit.percent', { value: row.varPct })}
        </bdi>
      ) : null}
    </div>
  );
}
